import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { sendOtp } from "../services/api";
import LoginAnimatedHeader from "../components/LoginAnimatedHeader";
import PhoneNumber from "../components/PhoneNumber";
import Checkbox from "../components/Checkbox";
import { PrimaryButton } from "../components/Button";
import SpannedText from "../components/SpannedText";
import styled from "styled-components";
import { useAuth } from "../contexts/AuthContext";

const HeaderText = styled.h1`
  font-family: "Manrope", sans-serif;
  font-weight: 700;
  font-style: normal;
  font-size: 20px;
  letter-spacing: 0%;
  text-align: center;
  color: #000000;
  margin-bottom: 12px;
  margin-top: 20px;
`;

const Login = ({ role = "student" }) => {
  // Detect country from browser location
  const detectCountry = () => {
    try {
      // Try to get country from timezone
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      // Check if timezone indicates India or Singapore
      if (timezone.includes('Calcutta') || timezone.includes('Kolkata') || timezone.includes('Mumbai') || timezone.includes('Delhi') || timezone.includes('Asia/Kolkata')) {
        return { code: '91', maxLength: 10, country: 'IN' };
      } else if (timezone.includes('Singapore') || timezone.includes('Asia/Singapore')) {
        return { code: '65', maxLength: 8, country: 'SG' };
      }

      // Fallback: Try to get country from locale
      const locale = Intl.DateTimeFormat().resolvedOptions().locale;
      if (locale.includes('IN') || locale.toLowerCase().includes('india')) {
        return { code: '91', maxLength: 10, country: 'IN' };
      } else if (locale.includes('SG') || locale.toLowerCase().includes('singapore')) {
        return { code: '65', maxLength: 8, country: 'SG' };
      }

      // Default to Singapore if can't detect
      return { code: '65', maxLength: 8, country: 'SG' };
    } catch (error) {
      console.error('Error detecting country:', error);
      // Default to Singapore
      return { code: '65', maxLength: 8, country: 'SG' };
    }
  };

  const countryInfo = detectCountry();
  const [countryCode, setCountryCode] = useState(countryInfo.code);
  const [maxDigits, setMaxDigits] = useState(countryInfo.maxLength);
  const [mobileNumber, setMobileNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const { isAuthenticated, hasRole } = useAuth();

  // Reload page after 1 second when component mounts (only first time)
  useEffect(() => {
    console.log('Login useEffect running...');

    // Clear any old format data first
    const oldValue = sessionStorage.getItem('loginPageReloaded');
    if (oldValue === 'true') {
      console.log('Clearing old format data...');
      sessionStorage.removeItem('loginPageReloaded');
    }

    // Check if we've already reloaded in this session using timestamp
    const lastReloadTime = sessionStorage.getItem('loginPageReloaded');
    const currentTime = Date.now();
    const oneMinuteAgo = currentTime - (60 * 1000); // 1 minute ago

    console.log('Last reload time:', lastReloadTime);
    console.log('Current time:', currentTime);
    console.log('One minute ago:', oneMinuteAgo);

    // If no reload time or reload was more than 1 minute ago, reload
    if (!lastReloadTime || parseInt(lastReloadTime) < oneMinuteAgo) {
      console.log('Setting up reload timer...');
      const timer = setTimeout(() => {
        console.log('Executing reload...');
        sessionStorage.setItem('loginPageReloaded', currentTime.toString());
        window.location.reload();
      }, 1000);

      return () => {
        console.log('Cleaning up timer...');
        clearTimeout(timer);
      };
    } else {
      console.log('Already reloaded recently, skipping...');
    }
  }, []);

  // Redirect to home if already authenticated (with delay to allow reload to happen first)
  useEffect(() => {
    const redirectTimer = setTimeout(() => {
      if (isAuthenticated()) {
        if (hasRole('student')) {
          navigate('/student/home', { replace: true });
        } else if (hasRole('trainer')) {
          navigate('/trainer/dashboard', { replace: true });
        }
      }
    }, 1500); // Wait 1.5 seconds to allow reload to happen first

    return () => clearTimeout(redirectTimer);
  }, [isAuthenticated, hasRole, navigate]);

  const validatePhoneNumber = (number) => {
    // Validate based on country: India (10 digits), Singapore (8 digits)
    if (countryCode === '91') {
      // India: exactly 10 digits
      return /^[0-9]{10}$/.test(number);
    } else if (countryCode === '65') {
      // Singapore: exactly 8 digits
      return /^[0-9]{8}$/.test(number);
    }
    // Fallback validation
    return /^[0-9]{8,10}$/.test(number);
  };

  const handleSendOtp = async () => {
    if (!validatePhoneNumber(mobileNumber)) {
      setError("Please enter a valid phone number.");
      return;
    }

    setLoading(true);
    try {
      const response = await sendOtp(countryCode, mobileNumber, role);
      console.log(response);
      setLoading(false);
      navigate(`/${role}/otp-verification`, {
        state: { countryCode, mobileNumber, role }
      });
    } catch (error) {
      setError("Failed to send OTP. Please try again.");
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    let value = e.target.value.replace(/[^0-9]/g, "");

    // Limit to maxDigits based on country
    if (maxDigits && value.length > maxDigits) {
      value = value.slice(0, maxDigits);
    }

    setMobileNumber(value);
    if (error) {
      setError(""); // Clear error when user starts typing
    }
  };

  // Handle back button press
  useEffect(() => {
    const handleBackButton = (e) => {
      if (isAuthenticated()) {
        if (hasRole('student')) {
          navigate('/student/home', { replace: true });
        } else if (hasRole('trainer')) {
          navigate('/trainer/dashboard', { replace: true });
        }
      }
    };

    // Listen for popstate (back button)
    window.addEventListener('popstate', handleBackButton);

    return () => {
      window.removeEventListener('popstate', handleBackButton);
    };
  }, [isAuthenticated, hasRole, navigate]);



  return (
    <div className="h-full flex flex-col items-center justify-start bg-white pb-6">
      <LoginAnimatedHeader />

      <HeaderText>Get trained at your place now</HeaderText>

      <div className="mb-8">
        <PhoneNumber
          onChange={handleInputChange}
          onFocus={() => setError("")}
          countryCode={`+${countryCode}`}
          maxLength={maxDigits}
        />
        {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
        <Checkbox />
      </div>
      <PrimaryButton
        label={loading ? "SENDING..." : "LOGIN"}
        onClick={handleSendOtp}
      />
      <SpannedText />
    </div>
  );
};

export default Login;
